import { FC, useState, useEffect } from 'react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { RtpNrsTrendChart } from './RtpNrsTrendChart';
import { RtpConfidenceFearChart } from './RtpConfidenceFearChart';
import { RtpPainZonesMap } from './RtpPainZonesMap';
import { RtpNotesHistory } from './RtpNotesHistory';

interface RtpMonitoringDashboardProps {
  athleteId: string;
}

interface RtpSessionLog {
  _id: string;
  athleteId: string;
  painNRS: number;
  painZones: string[];
  confidenceScore: number;
  fearScore: number;
  notes?: string;
  createdAt: string;
}

interface RtpTrendEntry {
  date: string;
  painNRS: number;
  confidenceScore: number;
  fearScore: number;
}

export const RtpMonitoringDashboard: FC<RtpMonitoringDashboardProps> = ({ athleteId }) => {
  const { getAthleteRtpSessionLogs, getRtpSessionTrends } = useEndpoints();
  const [sessionLogs, setSessionLogs] = useState<RtpSessionLog[]>([]);
  const [trends, setTrends] = useState<RtpTrendEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!athleteId) return;
    setLoading(true);
    Promise.all([
      getAthleteRtpSessionLogs(athleteId, { limit: 50 }),
      getRtpSessionTrends(athleteId),
    ])
      .then(([logsRes, trendsRes]) => {
        const logs = logsRes.data?.docs || logsRes.data || [];
        setSessionLogs(Array.isArray(logs) ? logs : []);
        const trendData = trendsRes.data?.trends || trendsRes.data || [];
        setTrends(Array.isArray(trendData) ? trendData : []);
      })
      .catch((err) => console.error('Error loading RTP data:', err))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteId]);

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center' }}>Caricamento dati RTP...</div>;
  }

  // Latest NRS
  const latestLog = sessionLogs.length > 0
    ? sessionLogs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
    : null;
  const latestNRS = latestLog?.painNRS ?? null;
  const isAlert = latestNRS !== null && latestNRS >= 7;

  // Aggregate pain zones with frequency
  const painZoneFrequency: Record<string, number> = {};
  sessionLogs.forEach((log) => {
    log.painZones?.forEach((zone) => {
      painZoneFrequency[zone] = (painZoneFrequency[zone] || 0) + 1;
    });
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Overview bar */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <div
          style={{
            padding: '12px 20px',
            borderRadius: 8,
            backgroundColor: isAlert ? '#FED7D7' : '#F0FFF4',
            flex: 1,
          }}
        >
          <div style={{ fontSize: 12, color: '#4A5568', marginBottom: 4 }}>Ultimo NRS</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: isAlert ? '#E53E3E' : '#38A169' }}>
            {latestNRS !== null ? latestNRS : '—'}
          </div>
          {latestLog && (
            <div style={{ fontSize: 11, color: '#A0AEC0' }}>
              {new Date(latestLog.createdAt).toLocaleDateString('it-IT')}
            </div>
          )}
        </div>
        {isAlert && (
          <div
            style={{
              padding: '12px 20px',
              borderRadius: 8,
              backgroundColor: '#FED7D7',
              color: '#C53030',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Attenzione: NRS elevato ({latestNRS}/10)
          </div>
        )}
        <div style={{ padding: '12px 20px', borderRadius: 8, backgroundColor: '#EBF8FF', flex: 1 }}>
          <div style={{ fontSize: 12, color: '#4A5568', marginBottom: 4 }}>Sessioni RTP</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#3182CE' }}>{sessionLogs.length}</div>
        </div>
      </div>

      {/* Charts grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Trend NRS</h4>
          <RtpNrsTrendChart data={trends} />
        </div>
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Fiducia / Paura</h4>
          <RtpConfidenceFearChart data={trends} />
        </div>
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Zone dolore</h4>
          <RtpPainZonesMap painZones={painZoneFrequency} />
        </div>
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Note sessioni</h4>
          <RtpNotesHistory sessionLogs={sessionLogs} />
        </div>
      </div>
    </div>
  );
};

export default RtpMonitoringDashboard;
